import React from "react";
import {
  DefaultButton,
  Dropdown,
  Icon,
  IconButton,
  IDropdownOption,
  ImageIcon,
  Label,
  Link,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
} from "@fluentui/react";
import xlsxImage from "../../assets/xlsx.svg";
import { BulkRemovalPanelProps } from "../../shared/models/BulkRemovalPanelProps.model";

const reasonForRemovalOptions: IDropdownOption[] = [
  { key: "ACCESSREVIEW", text: "Quarterly access reviews" },
  { key: "YEARLYPURGE", text: "Yearly user purge" },
  { key: "NOLONGERVALID", text: "Business group/role no longer valid" },
  { key: "OTHER", text: "Other" },
];

interface IBulkRemovalFormProps {
  bulkRemovalPanelProps: BulkRemovalPanelProps;
}

class BulkRemovalForm extends React.Component<IBulkRemovalFormProps> {
  fileInputRef: React.RefObject<HTMLInputElement>;
  requiredStyles = { color: "#A4262C", fontSize: 12 };
  requiredLabelStyles = { paddingBottom: 0 };

  constructor(props: IBulkRemovalFormProps) {
    super(props);
    this.fileInputRef = React.createRef<HTMLInputElement>();
  }

  RequiredMarker = (): JSX.Element => {
    return <span style={this.requiredStyles}> *</span>;
  };

  handleSelectFileButtonClick = () => {
    this.fileInputRef.current?.click();
  };

  render() {
    const {
      onFileInputChange,
      setReasonForRemovalChange,
      setReasonForRemovalCustomTextChange,
      reasonForRemoval,
      reasonForRemovalCustomText,
      setFormValidationError,
      formValidationError,
      dataLoaded,
      setDataLoaded,
      saveInProgress,
      setInputValue,
      reasonForRemovalError,
      setReasonForRemovalError,
      file,
      setFile,
      setFileErrorMessage,
      downloadFile,
    } = this.props.bulkRemovalPanelProps;

    return (
      <Stack tokens={{ childrenGap: 10 }}>
        {formValidationError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline>
            {formValidationError}
          </MessageBar>
        )}
        {saveInProgress && (
          <MessageBar messageBarType={MessageBarType.warning} isMultiline>
            Validating file. Please wait...
          </MessageBar>
        )}
        <Label style={this.requiredLabelStyles}>
          Reason for Removal <this.RequiredMarker />
        </Label>

        <Dropdown
          onChange={(
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption,
            index?: number
          ): void => {
            setReasonForRemovalChange(option?.text as string);
            setReasonForRemovalError(null);
          }}
          options={reasonForRemovalOptions}
          placeholder="Select an option"
          styles={{ root: { width: 300 } }}
          errorMessage={reasonForRemovalError}
        />
        {reasonForRemoval === "Other" && (
          <TextField
            label="Please specify"
            required
            value={reasonForRemovalCustomText}
            onChange={(
              e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
              newValue?: string
            ) => {
              setReasonForRemovalCustomTextChange(newValue || "");
              if (formValidationError) {
                setFormValidationError(null);
              }
            }}
            styles={{ root: { width: 300 } }}
            errorMessage={formValidationError}
          />
        )}

        <Label style={this.requiredLabelStyles} required>
          Bulk Request File
        </Label>

        <div
          style={{
            ...(dataLoaded && {
              backgroundColor: "#F3F2F1",
              padding: "10px 15px",
              width: 500,
              borderRadius: 5,
            }),
          }}
        >
          {!dataLoaded && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <DefaultButton
                type="button"
                onClick={this.handleSelectFileButtonClick}
                text="Select file to upload"
                style={{ width: 180 }}
                disabled={saveInProgress}
              />
            </div>
          )}

          {dataLoaded && file && (
            <Stack
              horizontal
              verticalAlign="center"
              horizontalAlign="space-between"
            >
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 10 }}
              >
                <ImageIcon
                  imageProps={{
                    src: xlsxImage,
                    width: 32,
                    height: 32,
                  }}
                />
                <Stack>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    {file.name}
                  </span>
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 5 }}
                  >
                    <span style={{ fontSize: 12, color: "#605E5C" }}>
                      {file.size < 1024 * 1024
                        ? `${Math.round(file.size / 1024)} KB`
                        : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                    </span>
                  </Stack>
                </Stack>
              </Stack>

              <IconButton
                iconProps={{ iconName: "Cancel" }}
                title="Remove file"
                ariaLabel="Remove file"
                onClick={() => {
                  setFile(null);
                  setInputValue("");
                  setDataLoaded(false);
                  setFormValidationError(null);
                  setFileErrorMessage(null);
                  if (this.fileInputRef.current) {
                    this.fileInputRef.current.value = "";
                  }
                }}
                styles={{ root: { color: "#605E5C" } }}
              />
            </Stack>
          )}
        </div>

        <Link
          href=""
          target="_blank"
          style={{
            marginTop: 10,
            fontSize: 14,
            color: "#0078D4",
            textDecoration: "none",
            borderRadius: 5,
            display: "inline-block",
            width: "fit-content",
          }}
          onClick={(e) => {
            e.preventDefault();
            downloadFile();
          }}
        >
          <span>
            Download Bulk Request Template
            <Icon iconName="Download" style={{ marginLeft: 5 }} />
          </span>
        </Link>

        <input
          type="file"
          ref={this.fileInputRef}
          style={{ display: "none" }}
          onChange={onFileInputChange}
        />
      </Stack>
    );
  }
}

export default BulkRemovalForm;
